import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import CalendarApi from '../../api/CalendarApi.js';
import ResourcesApi from '../../api/resourcesApi.js';
import {
	listUserCalendarFilters,
	createUserCalendarFilter,
	updateUserCalendarFilter,
	deleteUserCalendarFilter
} from '../../api/userCalendarFilterApi.js';
import { toApiFilter, fromApiFilter } from '../calendar-common/filter/filterMapping.js';
import { resolveTimezoneId } from '../calendar-common/resolveTimezoneId.js';
import { unwrap } from './utils/apiEnvelope.js';
import { normalizeEvent } from './utils/normalizeEvent.js';

/**
 * @typedef {Object} EventObject
 * @property {string|number} id
 * @property {string|number} pilotID
 * @property {string|number} [instructorID]
 * @property {string} organizationUnique
 * @property {string|number} resourceID
 * @property {boolean|number|string} [restricted]
 * @property {string} startTime  ISO8601 or epoch-string per legacy payload
 * @property {string} finishTime
 * @property {string} [type]
 * @property {boolean|number} [availableForEdit]
 *
 * @typedef {Object} DayBucket
 * @property {number} unixTimestamp
 * @property {Array<string|number>} eventIds
 *
 * @typedef {Object} CalendarMonthState
 * @property {{from: string|null, to: string|null, view: 'month'|'week'|'day'}} dateRange
 * @property {Object<string, DayBucket>} days
 * @property {Object<string, EventObject>} events
 * @property {{org: string|null, location: string|string[]|null, resource: string|string[]|null, pilot: string|string[]|null, instructor: string|string[]|null, searchQuery: string}} filters
 * @property {'idle'|'loading'|'succeeded'|'failed'} status
 * @property {string|null} error
 * @property {string|null} lastFetchedAt
 */

/** @type {CalendarMonthState} */
export const initialState = {
	dateRange: { from: null, to: null, view: 'month' },
	days: {},
	events: {},
	filters: {
		org: [],
		location: [],
		resource: [],
		pilot: [],
		instructor: [],
		timezone: 'UTC',
		maintenance: true,
		myonly: false,
		waiting: true,
		activityTypeColorScheme: false,
		searchQuery: ''
	},
	// One-shot guard read by sub-epic 10.4 — once the mount auto-selects the
	// current org, this flag prevents re-seeding mid-session if the user
	// manually unchecks everything.
	orgAutoSelectApplied: false,
	// One-shot gate flipped by the page once bootstrap has resolved and the
	// real org/user timezone has been seeded into `filters.timezone`. The
	// events effect waits for this so it fires ONCE with the resolved timezone
	// instead of first firing with the pre-bootstrap `'UTC'` default and then
	// refetching — eliminating the wasted UTC events request on load.
	timezoneResolved: false,
	status: 'idle',
	error: null,
	lastFetchedAt: null,
	// Tracks the latest `fetchMonthEvents` requestId so late responses from
	// previous dispatches can be discarded (race-safe navigation).
	latestRequestId: null,
	// YYYY-MM tag stamped in `.pending` alongside `latestRequestId`; the
	// `.fulfilled`/`.rejected` guards use it for defense-in-depth in case a
	// race between `setDateRange` and the next `.pending` sneaks a stale
	// response past the requestId check.
	pendingMonthTag: null,
	savedFilters: {
		items: [],
		activeId: null,
		loading: false,
		error: null
	}
};

/**
 * Unwrap a BaseApi response into a plain list of saved filters. Handles
 * both the double-envelope (`{ data: { data: [...], success: true } }`) and
 * the bare array shape seen in alternative controllers.
 */
function unwrapSavedFiltersList(response) {
	const payload = unwrap(response) || [];
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload.filters)) return payload.filters;
	if (Array.isArray(payload.items)) return payload.items;
	return [];
}

/**
 * Narrow a saved-filter envelope down to `{ id, name, filter }`. The
 * controller (`UserCalendarFilterController::addFilter` /
 * `updateFilter`) wraps the created/updated entity in an array —
 * `$this->setData([$filter])` → `{data: [{id, name, filter}]}` after
 * `unwrap()`. Accept both the bare object and the single-element array.
 */
function unwrapSavedFilter(response) {
	const payload = unwrap(response);
	if (!payload) return null;
	if (Array.isArray(payload)) {
		return payload.length > 0 ? payload[0] : null;
	}
	if (payload.filter && payload.id != null) return payload;
	if (payload.data) {
		return Array.isArray(payload.data) ? payload.data[0] || null : payload.data;
	}
	return payload;
}

/**
 * Defaults applied by `clearFilters`. Intentionally omits `timezone` — the
 * user's current timezone choice is preserved across clears so that clearing
 * the org/location/pilot filter does not also reset the timezone they spent
 * effort picking.
 */
const CLEARABLE_FILTER_DEFAULTS = {
	org: [],
	location: [],
	resource: [],
	pilot: [],
	instructor: [],
	maintenance: true,
	myonly: false,
	waiting: true,
	activityTypeColorScheme: false,
	searchQuery: ''
};

/**
 * Normalise the Symfony `BaseViewResponse` shape (`{ days: [...], events: [...] }`)
 * into the `{ days: { [unixTimestamp]: { unixTimestamp, eventIds[] } }, events: { [id]: ev } }`
 * shape this slice stores. Pure helper, exported for tests + thunk reuse.
 *
 * Symfony returns:
 *   days:   either an object keyed by unix-timestamp, or an array of
 *           `{ unixTimestamp, events: [...] }`.
 *   events: either an object keyed by id, or an array of `{ id, ... }`.
 * We accept either shape and produce the canonical store shape.
 *
 * @param {{days?: any, month?: any, events?: any}} payload
 * @returns {{days: Object<string, DayBucket>, events: Object<string, EventObject>}}
 */
export function normalizeCalendarPayload(payload = {}) {
	const days = {};
	const events = {};

	const rawEvents = payload.events;
	if (Array.isArray(rawEvents)) {
		rawEvents.forEach(ev => {
			if (ev && ev.id != null) events[String(ev.id)] = normalizeEvent(ev);
		});
	} else if (rawEvents && typeof rawEvents === 'object') {
		Object.entries(rawEvents).forEach(([id, ev]) => {
			if (ev) events[String(id)] = normalizeEvent(ev);
		});
	}

	const rawDays = payload.days || payload.month;
	const pushDay = (unixTimestamp, eventIds) => {
		const ts = Number(unixTimestamp);
		if (!Number.isFinite(ts)) return;
		const ids = (eventIds || [])
			.map(id => (id && typeof id === 'object' ? id.id : id))
			.filter(id => id != null)
			.map(String);
		days[String(ts)] = { unixTimestamp: ts, eventIds: ids };
	};

	if (Array.isArray(rawDays)) {
		rawDays.forEach(d => {
			if (!d) return;
			pushDay(d.unixTimestamp ?? d.timestamp ?? d.ts, d.events ?? d.eventIds ?? []);
		});
	} else if (rawDays && typeof rawDays === 'object') {
		Object.entries(rawDays).forEach(([ts, d]) => {
			if (!d) return;
			pushDay(ts, d.events ?? d.eventIds ?? d);
		});
	}

	return { days, events };
}

// ────────────────────────────────────────────────────────────────────────────
// API-param mapping
// ────────────────────────────────────────────────────────────────────────────

/**
 * Convert the slice's `filters` shape into the query-param object that the
 * Symfony `GET /calendar/events/{view}` endpoint expects.
 *
 * This is the SINGLE SOURCE OF TRUTH for three cross-layer concerns:
 *   1. Singular slice keys (`location`, `resource`, `pilot`, `instructor`,
 *      `org`) → plural DTO keys (`locations`, `resources`, `pilots`,
 *      `instructors`, `organizations`). The Symfony DTO ignores unknown
 *      keys (see `BaseCalendarRequestDto` + `EventsRequestDto`), so
 *      forwarding the singular forms silently broke server-side filtering
 *      in Rem 9 — this mapper restores the contract.
 *   2. `filters.org` is transitional: scalar `string|null` pre-10.2,
 *      `string[]` post-10.2. Both shapes collapse into an `organizations`
 *      array here so 10.2 can land without breaking transport.
 *   3. React state uses legacy name `myonly`; API expects camelCase `myOnly`.
 *   4. `searchQuery` is a local-only selector filter — it MUST NOT be sent.
 *
 * Explicit key list (NOT `Object.entries` iteration) — future slice fields
 * will not accidentally leak into the server request.
 *
 * @param {Object} [filters]
 * @returns {Object}
 */
export function mapFiltersToEventsParams(filters = {}, { orgSlugToId } = {}) {
	const out = {};

	// Multi-org support: resolve slug → numeric ID and emit `organizations[]`.
	// Backend `CalendarEventViewManager::setOrganizations` defaults to URL-path
	// org when the param is absent, which breaks multi-org selection. DTO
	// validates `/^\d+$/`, so slugs must be mapped to numeric IDs first.
	if (orgSlugToId && Array.isArray(filters.org) && filters.org.length > 0) {
		const ids = filters.org
			.map(slug => orgSlugToId[slug])
			.filter(id => id != null && id !== '')
			.map(id => Number(id))
			.filter(id => Number.isFinite(id));
		if (ids.length > 0) out.organizations = ids;
	}

	// Other multi-selects: singular slice key → plural DTO key. Drop empty
	// arrays so the backend treats `undefined` ≡ `[]` ≡ "no filter".
	if (Array.isArray(filters.location) && filters.location.length > 0) {
		out.locations = filters.location;
	}
	if (Array.isArray(filters.resource) && filters.resource.length > 0) {
		out.resources = filters.resource;
	}
	if (Array.isArray(filters.pilot) && filters.pilot.length > 0) {
		out.pilots = filters.pilot;
	}
	if (Array.isArray(filters.instructor) && filters.instructor.length > 0) {
		out.instructors = filters.instructor;
	}

	// Primitive pass-through. Use `!= null` so `false`/`0` survive.
	if (filters.timezone) out.timezone = filters.timezone;
	if (filters.maintenance != null) out.maintenance = filters.maintenance;
	if (filters.waiting != null) out.waiting = filters.waiting;
	if (filters.myonly != null) out.myOnly = filters.myonly;
	if (filters.activityTypeColorScheme != null) {
		out.activityTypeColorScheme = filters.activityTypeColorScheme;
	}

	return out;
}

// ────────────────────────────────────────────────────────────────────────────
// Thunks
// ────────────────────────────────────────────────────────────────────────────

/**
 * Fetch events for the current month and normalise them into the slice shape.
 *
 * @param {{view?: string, date?: string, filters?: object, timezone?: string}} args
 */
export const fetchMonthEvents = createAsyncThunk(
	'calendarMonth/fetchMonthEvents',
	async (args = {}, { getState, rejectWithValue }) => {
		const { view = 'month', date, filters = {}, timezone } = args;
		try {
			const orgsMap = getState()?.organizations || {};
			const orgSlugToId = {};
			Object.values(orgsMap).forEach(org => {
				if (!org) return;
				const slug = org.organizationUnique || org.unique;
				const id = org.organizationID ?? org.id;
				if (slug && id != null) orgSlugToId[slug] = id;
			});
			const mapped = mapFiltersToEventsParams(filters, { orgSlugToId });
			const params = {
				...mapped,
				...(timezone ? { timezone } : {}),
				...(date ? { date } : {}),
				compact: true
			};
			// Last gate before the wire: _buildQuery encodes once, so a surviving `%2F` ships as `%252F`.
			if (params.timezone) {
				params.timezone = resolveTimezoneId(params.timezone, 'UTC');
			}
			const response = await CalendarApi.listEvents(view, params);
			const payload = response?.data?.data ?? response?.data ?? response ?? {};
			return normalizeCalendarPayload(payload);
		} catch (err) {
			return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to fetch');
		}
	}
);

/**
 * Move an event with optimistic update + rollback on error.
 *
 * @param {{eventId: string|number, isMyEvent: boolean, fromDay: number, toDay: number, newStartTime: string, payload: object}} args
 */
export const moveEventThunk = createAsyncThunk(
	'calendarMonth/moveEvent',
	async (args, { dispatch, getState, rejectWithValue }) => {
		const { eventId, isMyEvent, fromDay, toDay, newStartTime, payload } = args;
		const slice = getState().calendarMonth || initialState;
		// Snapshot pre-move state for rollback
		const originalEvent = slice.events[String(eventId)];
		const originalState = {
			event: originalEvent ? { ...originalEvent } : null,
			fromDayBucket: slice.days[String(fromDay)]
				? { ...slice.days[String(fromDay)], eventIds: [...slice.days[String(fromDay)].eventIds] }
				: null,
			toDayBucket: slice.days[String(toDay)]
				? { ...slice.days[String(toDay)], eventIds: [...slice.days[String(toDay)].eventIds] }
				: null
		};

		dispatch(applyOptimisticMove({ eventId, fromDay, toDay, newStartTime }));

		try {
			const isMaintenance = (originalEvent?.type || '').toLowerCase().includes('maint');
			let apiCall;
			if (isMaintenance) {
				const resourceId = originalEvent?.resourceID;
				apiCall = ResourcesApi.moveMaintenance(
					resourceId,
					eventId,
					{
						startTime: payload.startTime,
						finishTime: payload.finishTime,
						// Resource location tz (NOT filter/browser tz). Per Rem17,
						// Symfony reinterprets the formatted wall-clock in the
						// resource location, so filter-tz formatting shifts the
						// time. Drag-drop payload doesn't carry locationTimezoneId
						// yet — pull from the original event's normalized shape
						// (`mapEventToFormState` writes `locationTimezoneId`).
						timezoneId:
							originalEvent?.locationTimezoneId || payload.locationTimezoneId || payload.timezone
					},
					originalEvent?.organizationUnique
				);
			} else {
				apiCall = isMyEvent
					? CalendarApi.moveMyEvent(eventId, payload)
					: CalendarApi.moveEvent(eventId, payload);
			}
			const response = await apiCall;
			return { eventId, response };
		} catch (err) {
			dispatch(revertOptimisticMove({ eventId, originalState }));
			return rejectWithValue(err?.response?.data?.error || err?.message || 'Move failed');
		}
	}
);

/**
 * Delete an event (single or recurring set). Not consumed by Phase 1 UI but
 * exposed for use by the existing event modals.
 *
 * @param {{eventId: string|number, params?: object}} args
 */
export const deleteEventThunk = createAsyncThunk(
	'calendarMonth/deleteEvent',
	async ({ eventId, params } = {}, { rejectWithValue }) => {
		try {
			const response = await CalendarApi.deleteEvent(eventId, params);
			return { eventId, response };
		} catch (err) {
			return rejectWithValue(err?.response?.data?.error || err?.message || 'Delete failed');
		}
	}
);

// ────────────────────────────────────────────────────────────────────────────
// Saved filter thunks (sub-epic 9.4)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Fetch every saved filter for the current user and narrow to those scoped
 * to the requested `view` via the `filter.view` key. Filters without a
 * `view` key (legacy) are ignored — they still exist in the DB but only
 * appear in the classic Twig Month/Week views.
 *
 * @param {'month'|'week'|'day'} view
 */
export const loadSavedFilters = createAsyncThunk(
	'calendarMonth/loadSavedFilters',
	async (view = 'month', { rejectWithValue }) => {
		try {
			const response = await listUserCalendarFilters();
			const all = unwrapSavedFiltersList(response);
			const items = all
				.map(item => {
					if (!item || item.id == null) return null;
					const filter = item.filter || {};
					return { id: item.id, name: item.name || '', filter };
				})
				.filter(Boolean)
				.filter(f => f.filter && f.filter.view === view);
			return items;
		} catch (err) {
			return rejectWithValue(
				err?.response?.data?.error || err?.message || 'Failed to load saved filters'
			);
		}
	}
);

/**
 * Persist the current slice `filters` as a new saved filter, embed the
 * `view` discriminator in the JSON blob, and append the result to the slice
 * state so the dropdown immediately shows it.
 *
 * @param {{name: string, view: 'month'|'week'|'day', organizationMap?: object}} args
 */
export const saveCurrentFilter = createAsyncThunk(
	'calendarMonth/saveCurrentFilter',
	async ({ name, view, organizationMap = {} } = {}, { getState, rejectWithValue }) => {
		if (!name || !name.trim()) return rejectWithValue('Filter name is required');
		try {
			const filters = (getState().calendarMonth || initialState).filters;
			const apiFilter = toApiFilter(filters, view, organizationMap);
			const response = await createUserCalendarFilter(name.trim(), apiFilter);
			const created = unwrapSavedFilter(response);
			if (!created || created.id == null) {
				return rejectWithValue('Unexpected response from /user/filter');
			}
			return { id: created.id, name: created.name || name.trim(), filter: apiFilter };
		} catch (err) {
			return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to save filter');
		}
	}
);

/**
 * Overwrite an existing saved filter with the current slice `filters`.
 *
 * @param {{id: number|string, name: string, view: 'month'|'week'|'day', organizationMap?: object}} args
 */
export const updateSavedFilter = createAsyncThunk(
	'calendarMonth/updateSavedFilter',
	async ({ id, name, view, organizationMap = {} } = {}, { getState, rejectWithValue }) => {
		if (id == null) return rejectWithValue('Missing filter id');
		if (!name || !name.trim()) return rejectWithValue('Filter name is required');
		try {
			const filters = (getState().calendarMonth || initialState).filters;
			const apiFilter = toApiFilter(filters, view, organizationMap);
			await updateUserCalendarFilter(id, name.trim(), apiFilter);
			return { id, name: name.trim(), filter: apiFilter };
		} catch (err) {
			return rejectWithValue(
				err?.response?.data?.error || err?.message || 'Failed to update filter'
			);
		}
	}
);

/**
 * Delete a saved filter by id.
 *
 * @param {{id: number|string}} args
 */
export const deleteSavedFilter = createAsyncThunk(
	'calendarMonth/deleteSavedFilter',
	async ({ id } = {}, { rejectWithValue }) => {
		if (id == null) return rejectWithValue('Missing filter id');
		try {
			await deleteUserCalendarFilter(id);
			return { id };
		} catch (err) {
			return rejectWithValue(
				err?.response?.data?.error || err?.message || 'Failed to delete filter'
			);
		}
	}
);

// ────────────────────────────────────────────────────────────────────────────
// Stale-response guards (pure helpers — unit tested)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Extract the `YYYY-MM` month tag from either an ISO datetime
 * (`2026-04-01T00:00:00.000Z`) or a `YYYY-MM-DD` date string.
 * Returns `null` when the input doesn't look like a date.
 */
export function monthTagFromIsoLike(value) {
	if (typeof value !== 'string' || value.length < 7) return null;
	const head = value.slice(0, 7);
	return /^\d{4}-\d{2}$/.test(head) ? head : null;
}

/**
 * Return true when a resolved/rejected `fetchMonthEvents` action carries a
 * `requestId` that no longer matches `state.latestRequestId` — i.e. a later
 * dispatch has already taken over. TZ- and payload-shape-independent.
 *
 * Used as the first of two guards in `.fulfilled`/`.rejected`; see
 * `isStaleFetchResponseByMonth` for the second (catches the reducer-ordering
 * gap between `setDateRange` and the next `.pending`).
 *
 * Pure — exported for unit testing.
 */
export function isStaleFetchResponse(action, state) {
	const actionId = action?.meta?.requestId;
	const latestId = state?.latestRequestId;
	// If either id is missing treat as non-stale so the first resolution
	// after a fresh mount isn't accidentally dropped.
	if (!actionId || !latestId) return false;
	return actionId !== latestId;
}

/**
 * Return true when the response's month tag (`action.meta.arg.date` →
 * `YYYY-MM`) no longer matches the month of the latest in-flight request
 * (`state.pendingMonthTag`). Compared against `pendingMonthTag` — NOT
 * `state.dateRange.from` — because `dateRange.from` is stored as
 * `toISOString()` of local midnight, which TZ-shifts to the previous
 * month for any timezone east of UTC (e.g. Ukraine UTC+3:
 * `new Date(2026, 3, 1).toISOString() === '2026-03-31T21:00:00.000Z'`).
 * `pendingMonthTag` is normalized from the same `arg.date` at `.pending`
 * time, so the comparison is TZ-free and race-correct.
 *
 * Null-safe: if either tag can't be extracted, treat as non-stale.
 *
 * Pure — exported for unit testing.
 */
export function isStaleFetchResponseByMonth(action, state) {
	const argTag = monthTagFromIsoLike(action?.meta?.arg?.date);
	const pendingTag = state?.pendingMonthTag;
	if (!argTag || !pendingTag) return false;
	return argTag !== pendingTag;
}

// ────────────────────────────────────────────────────────────────────────────
// Slice
// ────────────────────────────────────────────────────────────────────────────

const calendarMonthSlice = createSlice({
	name: 'calendarMonth',
	initialState,
	reducers: {
		setDateRange(state, action) {
			const { from, to, view } = action.payload || {};
			if (from !== undefined) state.dateRange.from = from;
			if (to !== undefined) state.dateRange.to = to;
			if (view !== undefined) state.dateRange.view = view;
		},

		setFilters(state, action) {
			state.filters = { ...state.filters, ...(action.payload || {}) };
		},

		// Flip the one-shot gate that releases the events fetch (see
		// `timezoneResolved` in initialState). Idempotent.
		markTimezoneResolved(state) {
			state.timezoneResolved = true;
		},

		clearFilters(state, action) {
			// Preserve the user's current timezone pick across clears; all
			// other filter keys reset to their defaults. Per Rem 10.2: `org`
			// re-seeds to `[currentOrgUnique]` (passed in the action payload)
			// so Clear matches the legacy "reload" behaviour — user gets
			// their own organization back automatically.
			const currentOrgUnique = action?.payload?.currentOrgUnique || null;
			state.filters = {
				...state.filters,
				...CLEARABLE_FILTER_DEFAULTS,
				org: currentOrgUnique ? [currentOrgUnique] : []
			};
			// A manual clear also drops the active-saved-filter selection.
			state.savedFilters.activeId = null;
		},

		/**
		 * Apply a saved filter to `state.filters` and mark it active. Lives in
		 * the reducers (not a thunk) because no async work is required — the
		 * blob has already been fetched by `loadSavedFilters`.
		 *
		 * Rem30 Item 3: the calendar is locked to the dashboard org. Callers
		 * pass `lockOrg` (the dashboard org slug) so a saved-filter blob that
		 * carries a different `org` can never switch organizations — the
		 * locked org overrides whatever the blob holds.
		 */
		selectSavedFilter(state, action) {
			const { id, lockOrg } = action.payload || {};
			if (id == null) return;
			const hit = state.savedFilters.items.find(f => String(f.id) === String(id));
			if (!hit) return;
			state.filters = { ...state.filters, ...fromApiFilter(hit.filter || {}) };
			if (lockOrg) {
				state.filters.org = [lockOrg];
			}
			state.savedFilters.activeId = hit.id;
		},

		clearActiveSavedFilter(state) {
			state.savedFilters.activeId = null;
		},

		/** Restore a persisted activeId from sessionStorage on page mount.
		 *  Does NOT re-apply filters (setFilters handles that separately).
		 *  loadSavedFilters.fulfilled validates the id against loaded items and
		 *  clears it if stale. */
		setActiveFilterId(state, action) {
			state.savedFilters.activeId = action.payload ?? null;
		},

		/**
		 * Sub-epic 10.4. Flip the one-shot `orgAutoSelectApplied` guard after
		 * the mount effect has had its chance to seed `filters.org` with the
		 * current organization. Once set, the mount effect never re-seeds
		 * during the same session — the user is free to clear the org
		 * checkbox and see "no org" results without the effect fighting back.
		 */
		markOrgAutoSelectApplied(state) {
			state.orgAutoSelectApplied = true;
		},

		normalizeEvents(state, action) {
			const { days = {}, events = {} } = action.payload || {};
			state.days = days;
			state.events = events;
		},

		applyOptimisticMove(state, action) {
			const { eventId, fromDay, toDay, newStartTime } = action.payload || {};
			const id = String(eventId);
			const fromKey = String(fromDay);
			const toKey = String(toDay);
			const event = state.events[id];
			if (!event) return;

			// Update event fields
			if (newStartTime !== undefined) state.events[id].startTime = newStartTime;
			state.events[id]._optimistic = true;

			// Remove from old day bucket
			if (state.days[fromKey]) {
				state.days[fromKey].eventIds = state.days[fromKey].eventIds.filter(
					eid => String(eid) !== id
				);
			}

			// Insert into new day bucket (creating it if needed)
			if (!state.days[toKey]) {
				const ts = Number(toDay);
				state.days[toKey] = {
					unixTimestamp: Number.isFinite(ts) ? ts : 0,
					eventIds: []
				};
			}
			if (!state.days[toKey].eventIds.map(String).includes(id)) {
				state.days[toKey].eventIds.push(id);
			}
		},

		revertOptimisticMove(state, action) {
			const { eventId, originalState } = action.payload || {};
			if (!originalState) return;
			const id = String(eventId);

			if (originalState.event) {
				state.events[id] = { ...originalState.event };
			}
			if (originalState.fromDayBucket) {
				state.days[String(originalState.fromDayBucket.unixTimestamp)] = {
					...originalState.fromDayBucket,
					eventIds: [...originalState.fromDayBucket.eventIds]
				};
			}
			if (originalState.toDayBucket) {
				state.days[String(originalState.toDayBucket.unixTimestamp)] = {
					...originalState.toDayBucket,
					eventIds: [...originalState.toDayBucket.eventIds]
				};
			}
			if (state.events[id]) delete state.events[id]._optimistic;
		}
	},
	extraReducers: builder => {
		builder
			.addCase(fetchMonthEvents.pending, (state, action) => {
				state.status = 'loading';
				state.error = null;
				// Stamp both the latest in-flight requestId and its month tag.
				// `.fulfilled`/`.rejected` combine both signals (plus a third
				// check against the currently displayed dateRange) to drop
				// stale responses under every known race pattern.
				state.latestRequestId = action.meta.requestId;
				state.pendingMonthTag = monthTagFromIsoLike(action.meta.arg?.date);
			})
			.addCase(fetchMonthEvents.fulfilled, (state, action) => {
				// Combined guard:
				// (a) `isStaleFetchResponse` — another dispatch has pended.
				// (b) `isStaleFetchResponseByMonth` — `setDateRange` moved the
				//     UI to a different month before the next `.pending` fired
				//     (the gap Reviewer flagged).
				if (isStaleFetchResponse(action, state)) return;
				if (isStaleFetchResponseByMonth(action, state)) return;
				state.status = 'succeeded';
				state.error = null;
				state.lastFetchedAt = new Date().toISOString();
				state.days = action.payload.days || {};
				state.events = action.payload.events || {};
			})
			.addCase(fetchMonthEvents.rejected, (state, action) => {
				if (isStaleFetchResponse(action, state)) return;
				if (isStaleFetchResponseByMonth(action, state)) return;
				state.status = 'failed';
				state.error = action.payload || action.error?.message || 'Unknown error';
			})
			// ── Saved filters (sub-epic 9.4) ─────────────────────────────────
			.addCase(loadSavedFilters.pending, state => {
				state.savedFilters.loading = true;
				state.savedFilters.error = null;
			})
			.addCase(loadSavedFilters.fulfilled, (state, action) => {
				state.savedFilters.loading = false;
				state.savedFilters.items = action.payload || [];
				// If the active id is no longer in the list (e.g. concurrent
				// delete from another tab), drop the selection.
				if (
					state.savedFilters.activeId != null &&
					!state.savedFilters.items.find(f => String(f.id) === String(state.savedFilters.activeId))
				) {
					state.savedFilters.activeId = null;
				}
			})
			.addCase(loadSavedFilters.rejected, (state, action) => {
				state.savedFilters.loading = false;
				state.savedFilters.error =
					action.payload || action.error?.message || 'Failed to load saved filters';
			})
			.addCase(saveCurrentFilter.fulfilled, (state, action) => {
				if (!action.payload) return;
				state.savedFilters.items.push(action.payload);
				state.savedFilters.activeId = action.payload.id;
				state.savedFilters.error = null;
			})
			.addCase(saveCurrentFilter.rejected, (state, action) => {
				state.savedFilters.error =
					action.payload || action.error?.message || 'Failed to save filter';
			})
			.addCase(updateSavedFilter.fulfilled, (state, action) => {
				if (!action.payload) return;
				const idx = state.savedFilters.items.findIndex(
					f => String(f.id) === String(action.payload.id)
				);
				if (idx >= 0) {
					state.savedFilters.items[idx] = action.payload;
				}
				state.savedFilters.activeId = action.payload.id;
				state.savedFilters.error = null;
			})
			.addCase(updateSavedFilter.rejected, (state, action) => {
				state.savedFilters.error =
					action.payload || action.error?.message || 'Failed to update filter';
			})
			.addCase(deleteSavedFilter.fulfilled, (state, action) => {
				const id = action.payload && action.payload.id;
				if (id == null) return;
				state.savedFilters.items = state.savedFilters.items.filter(
					f => String(f.id) !== String(id)
				);
				if (String(state.savedFilters.activeId) === String(id)) {
					state.savedFilters.activeId = null;
				}
				state.savedFilters.error = null;
			})
			.addCase(deleteSavedFilter.rejected, (state, action) => {
				state.savedFilters.error =
					action.payload || action.error?.message || 'Failed to delete filter';
			});
	}
});

export const {
	setDateRange,
	setFilters,
	markTimezoneResolved,
	clearFilters,
	selectSavedFilter,
	clearActiveSavedFilter,
	setActiveFilterId,
	markOrgAutoSelectApplied,
	normalizeEvents,
	applyOptimisticMove,
	revertOptimisticMove
} = calendarMonthSlice.actions;

export default calendarMonthSlice.reducer;
